import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const QuickActions = () => {
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [isRunningSimulation, setIsRunningSimulation] = useState(false);

  const primaryActions = [
    {
      id: 'compliance-report',
      title: 'Generate Compliance Report',
      description: 'Create EU ETS surrender report',
      icon: 'FileText',
      color: 'primary',
      path: '/compliance-reports',
      action: 'generate'
    },
    {
      id: 'eua-trading',
      title: 'EUA Trading Hub',
      description: 'Access carbon allowance trading',
      icon: 'TrendingUp',
      color: 'success',
      path: '/eua-trading-hub',
      action: 'navigate'
    },
    {
      id: 'fleet-simulation',
      title: 'Run Fleet Simulation',
      description: 'Scenario analysis and optimization',
      icon: 'Zap',
      color: 'accent',
      path: '/voyage-planning',
      action: 'simulate'
    }
  ];

  const secondaryActions = [
    {
      id: 'emissions-calculator',
      title: 'Emissions Calculator',
      description: 'Calculate voyage emissions',
      icon: 'Calculator',
      path: '/emissions-calculator'
    },
    {
      id: 'vessel-performance',
      title: 'Vessel Performance',
      description: 'Analyze fleet efficiency',
      icon: 'BarChart3',
      path: '/vessel-performance'
    },
    {
      id: 'user-management',
      title: 'User Management',
      description: 'Manage team access',
      icon: 'Users',
      path: '/user-management'
    }
  ];

  const recentAlerts = [
    {
      id: 1,
      type: 'warning',
      title: 'EUA Shortage Alert',
      message: 'Fleet requires 2,450 additional allowances for Q4',
      timestamp: '5 min ago',
      action: 'Trade EUA',
      actionPath: '/eua-trading-hub'
    },
    {
      id: 2,
      type: 'error',
      title: 'D Rating Risk',
      message: 'MV Atlantic Voyager approaching D rating threshold',
      timestamp: '12 min ago',
      action: 'View Details',
      actionPath: '/vessel-performance'
    },
    {
      id: 3,
      type: 'info',
      title: 'Regulatory Update',
      message: 'New IMO guidelines effective January 2025',
      timestamp: '1 hour ago',
      action: 'Review',
      actionPath: '/compliance-reports'
    }
  ];

  const handleGenerateReport = async () => {
    setIsGeneratingReport(true);
    // Simulate report generation
    setTimeout(() => {
      setIsGeneratingReport(false);
      console.log('Compliance report generated');
    }, 3000);
  };

  const handleRunSimulation = async () => {
    setIsRunningSimulation(true);
    // Simulate fleet simulation
    setTimeout(() => {
      setIsRunningSimulation(false);
      console.log('Fleet simulation completed');
    }, 5000);
  };

  const handleActionClick = (action) => {
    switch (action?.action) {
      case 'generate':
        handleGenerateReport();
        break;
      case 'simulate':
        handleRunSimulation();
        break;
      default:
        // Navigation handled by Link component
        break;
    }
  };

  const getActionStyles = (color) => {
    const styles = {
      primary: 'bg-primary text-primary-foreground hover:bg-primary/90',
      success: 'bg-success text-success-foreground hover:bg-success/90',
      accent: 'bg-accent text-accent-foreground hover:bg-accent/90',
      warning: 'bg-warning text-warning-foreground hover:bg-warning/90'
    };
    return styles?.[color] || styles?.primary;
  };

  const getAlertStyles = (type) => {
    const styles = {
      error: 'border-l-error bg-error/5',
      warning: 'border-l-warning bg-warning/5',
      info: 'border-l-accent bg-accent/5'
    };
    return styles?.[type] || styles?.info;
  };

  const getAlertIcon = (type) => {
    const icons = {
      error: 'AlertCircle',
      warning: 'AlertTriangle',
      info: 'Info'
    };
    return icons?.[type] || 'Bell';
  };

  return (
    <div className="space-y-6">
      {/* Primary Actions */}
      <div className="maritime-card p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Quick Actions</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {primaryActions?.map((action) => (
            <div key={action?.id}>
              {action?.action === 'navigate' ? (
                <Link to={action?.path} className="block">
                  <div className={`p-4 rounded-lg transition-maritime cursor-pointer ${getActionStyles(action?.color)}`}>
                    <div className="flex items-center space-x-3 mb-2">
                      <Icon name={action?.icon} size={24} strokeWidth={2} />
                      <h4 className="font-semibold">{action?.title}</h4>
                    </div>
                    <p className="text-sm opacity-90">{action?.description}</p>
                  </div>
                </Link>
              ) : (
                <button
                  onClick={() => handleActionClick(action)}
                  disabled={
                    (action?.action === 'generate' && isGeneratingReport) ||
                    (action?.action === 'simulate' && isRunningSimulation)
                  }
                  className={`w-full p-4 rounded-lg transition-maritime ${getActionStyles(action?.color)} disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  <div className="flex items-center space-x-3 mb-2">
                    {((action?.action === 'generate' && isGeneratingReport) ||
                      (action?.action === 'simulate' && isRunningSimulation)) ? (
                      <div className="animate-spin">
                        <Icon name="Loader2" size={24} strokeWidth={2} />
                      </div>
                    ) : (
                      <Icon name={action?.icon} size={24} strokeWidth={2} />
                    )}
                    <h4 className="font-semibold">{action?.title}</h4>
                  </div>
                  <p className="text-sm opacity-90">{action?.description}</p>
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
      {/* Secondary Actions */}
      <div className="maritime-card p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">More Tools</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {secondaryActions?.map((action) => (
            <Link key={action?.id} to={action?.path}>
              <div className="p-4 border border-border rounded-lg hover:bg-muted transition-maritime">
                <div className="flex items-center space-x-3 mb-2">
                  <Icon name={action?.icon} size={20} strokeWidth={2} className="text-primary" />
                  <h4 className="font-medium text-foreground">{action?.title}</h4>
                </div>
                <p className="text-sm text-muted-foreground">{action?.description}</p>
              </div>
            </Link>
          ))}
        </div>
      </div>
      {/* Recent Alerts */}
      <div className="maritime-card p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground">Recent Alerts</h3>
          <Button variant="ghost" size="sm" iconName="Bell" iconPosition="left">
            View All
          </Button>
        </div>
        
        <div className="space-y-3">
          {recentAlerts?.map((alert) => (
            <div
              key={alert?.id}
              className={`p-4 border-l-4 rounded-r-lg ${getAlertStyles(alert?.type)}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-3">
                  <Icon 
                    name={getAlertIcon(alert?.type)} 
                    size={18} 
                    strokeWidth={2}
                    className={`mt-0.5 ${
                      alert?.type === 'error' ? 'text-error' :
                      alert?.type === 'warning' ? 'text-warning' : 'text-accent'
                    }`}
                  />
                  <div className="flex-1">
                    <h4 className="font-medium text-foreground text-sm">
                      {alert?.title}
                    </h4>
                    <p className="text-sm text-muted-foreground mt-1">
                      {alert?.message}
                    </p>
                    <p className="text-xs text-muted-foreground mt-2">
                      {alert?.timestamp}
                    </p>
                  </div>
                </div>
                
                <Link to={alert?.actionPath}>
                  <Button variant="outline" size="sm">
                    {alert?.action}
                  </Button>
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default QuickActions;